import { createContext, FC, useContext, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useIdEventListener, type IdEventListener, type EventListenerMap } from "@/hooks/useEventListeners";
import { useNavigatorDriver, useNavigatorApi } from "@/contexts/navigator";
import { useTabs, makeTabId } from "@/contexts/tabsContext";

/** Events emitted per workspace ID */
export interface IPreviewBrowserEvents extends EventListenerMap {
  /** Driver → tab: open/swap to this filePath (DB path with workspaceId prefix) */
  navigate: (filePath: string) => void;
  /** Tab → provider: iframe navigated to this URL path (from postMessage) */
  navigated: (path: string) => void;
}

export type PreviewBrowserContextType = {
  /** Per-workspace event bus */
  events: IdEventListener<IPreviewBrowserEvents>;
  /** Current URL path for a workspace (e.g. "/docs/index.html"), "" if none */
  getCurrentPath: (workspaceId: string) => string;
  /** Navigation controls — operate on the registered iframe for the workspace */
  goBack: (workspaceId: string) => void;
  goForward: (workspaceId: string) => void;
  reload: (workspaceId: string) => void;
  /** Register/unregister the iframe element (called by the tab on mount/unmount) */
  setIframe: (workspaceId: string, iframe: HTMLIFrameElement | null) => void;
  /** Update the stored path after in-iframe navigation (postMessage) */
  reportPath: (workspaceId: string, path: string) => void;
};

export const PreviewBrowserContext = createContext<PreviewBrowserContextType | null>(null);

export const usePreviewBrowserContext = (): PreviewBrowserContextType => {
  const ctx = useContext(PreviewBrowserContext);
  if (!ctx) throw new Error("usePreviewBrowserContext must be used within PreviewBrowserProvider");
  return ctx;
};

export const PreviewBrowserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const events = useIdEventListener<IPreviewBrowserEvents>("previewBrowser");
  const nav = useNavigatorApi();
  const tabs = useTabs();

  /** workspaceId → current URL path (e.g. "/docs/index.html") */
  const pathsRef = useRef<Map<string, string>>(new Map());
  /** workspaceId → mounted iframe element */
  const iframesRef = useRef<Map<string, HTMLIFrameElement>>(new Map());

  // ── Persistence helpers ───────────────────────────────────
  // Survive page reloads by mirroring pathsRef to sessionStorage.
  // Keyed per workspace so multiple preview-browser tabs restore independently.

  const STORAGE_PREFIX = "preview-browser-path:";

  const persistPath = (workspaceId: string, path: string) => {
    pathsRef.current.set(workspaceId, path);
    try { sessionStorage.setItem(STORAGE_PREFIX + workspaceId, path); } catch { /* quota/full — non-fatal */ }
  };

  const readPersistedPath = (workspaceId: string): string => {
    // Prefer in-memory (same session, already set by driver or postMessage)
    const mem = pathsRef.current.get(workspaceId);
    if (mem) return mem;
    // Fall back to sessionStorage (page reload)
    try {
      const stored = sessionStorage.getItem(STORAGE_PREFIX + workspaceId);
      if (stored) {
        pathsRef.current.set(workspaceId, stored); // warm the cache
        return stored;
      }
    } catch { /* sessionStorage unavailable */ }
    return "";
  };

  // ── Navigator driver ──────────────────────────────────────
  // Handles both cases: tab open (emit navigate → tab swaps src) and
  // tab closed (store path → tab reads it on mount via getCurrentPath).
  useNavigatorDriver((route) => {
    if (route.route !== "preview-browser") return;
    nav.clearRoute();

    const { workspaceId, filePath } = route.props;

    // Store current path for mount-time reads (and persist for reloads)
    const prefix = `${workspaceId}/`;
    const urlPath = filePath.startsWith(prefix) ? `/${filePath.slice(prefix.length)}` : `/${filePath}`;
    persistPath(workspaceId, urlPath);

    // Notify an already-mounted tab to swap its iframe src
    events.emit[workspaceId].navigate(filePath);

    // Open or focus the tab — reuseExisting keeps one tab per workspace
    const relativeTo = makeTabId("workspace", workspaceId);
    tabs.open("preview-browser", workspaceId, {
      relativeTo,
      placement: "split-right",
      scope: "parent",
      reuseExisting: true,
    });
  });

  const getCurrentPath = useCallback((workspaceId: string): string => {
    return readPersistedPath(workspaceId);
  }, []);

  const setIframe = useCallback((workspaceId: string, iframe: HTMLIFrameElement | null) => {
    if (iframe) {
      iframesRef.current.set(workspaceId, iframe);
    } else {
      iframesRef.current.delete(workspaceId);
    }
  }, []);

  // Back/forward: the sandbox blocks direct contentWindow.history access,
  // so we post a command to the iframe and let the injected script call
  // history.back()/forward() internally (same-origin, full access).
  const goBack = useCallback((workspaceId: string) => {
    iframesRef.current.get(workspaceId)?.contentWindow?.postMessage({ type: "preview-back" }, "*");
  }, []);

  const goForward = useCallback((workspaceId: string) => {
    iframesRef.current.get(workspaceId)?.contentWindow?.postMessage({ type: "preview-forward" }, "*");
  }, []);

  // Reload: send a command to the iframe so location.reload() fires internally.
  // Setting iframe.src directly would push a new history entry — reload must not.
  const reload = useCallback((workspaceId: string) => {
    iframesRef.current.get(workspaceId)?.contentWindow?.postMessage({ type: "preview-reload" }, "*");
  }, []);

  // Tab reports in-iframe navigation (from postMessage) — persist so reloads restore correctly
  const reportPath = useCallback((workspaceId: string, path: string) => {
    persistPath(workspaceId, path);
  }, []);

  const api = useMemo<PreviewBrowserContextType>(() => ({
    events,
    getCurrentPath,
    goBack,
    goForward,
    reload,
    setIframe,
    reportPath,
  }), [events, getCurrentPath, goBack, goForward, reload, setIframe, reportPath]);

  return (
    <PreviewBrowserContext.Provider value={api}>
      {children}
    </PreviewBrowserContext.Provider>
  );
};
